import { useMemo } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";
import { EmptyChartState } from "@/components/common/EmptyChartState";
import { useChartPalette } from "../utils/chartUtils";
import { getChartTooltipStyle } from "../utils/chartStyles";
import { useAppTheme } from "@/hooks/useAppTheme";
import type { VendorDistributionPoint } from "@/types";

interface RxDistributionChartProps {
  data: VendorDistributionPoint[];
}

/**
 * Donut chart showing the distribution of active policies by Rx/PBM vendor.
 */
export const RxDistributionChart = ({ data }: RxDistributionChartProps) => {
  const { theme } = useAppTheme();
  const chartPalette = useChartPalette();

  const chartData = useMemo(
    () =>
      data
        .filter((d) => d.policy_count > 0)
        .map((d) => ({ name: d.vendor_name, value: d.policy_count }))
        .sort((a, b) => b.value - a.value),
    [data]
  );

  if (chartData.length === 0) return <EmptyChartState />;

  const total = chartData.reduce((sum, d) => sum + d.value, 0);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <ResponsiveContainer width="100%" height={180}>
        <PieChart>
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={45}
            outerRadius={70}
            paddingAngle={2}
            strokeWidth={0}
          >
            {chartData.map((_, i) => (
              <Cell key={i} fill={chartPalette[i % chartPalette.length]} />
            ))}
          </Pie>
          <Tooltip contentStyle={getChartTooltipStyle(theme)} />
          <text
            x="50%"
            y="48%"
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={20}
            fontWeight={700}
            fill="currentColor"
          >
            {total}
          </text>
          <text x="50%" y="60%" textAnchor="middle" dominantBaseline="middle" fontSize={10} fill="gray">
            policies
          </text>
        </PieChart>
      </ResponsiveContainer>

      <Stack spacing={0.5} sx={{ px: 1, flex: 1, minHeight: 0, overflowY: "auto" }}>
        {chartData.map((entry, i) => (
          <Stack
            key={entry.name}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ flexShrink: 0 }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: chartPalette[i % chartPalette.length],
                  flexShrink: 0,
                }}
              />
              <Typography variant="caption" color="text.secondary" noWrap>
                {entry.name}
              </Typography>
            </Stack>
            <Typography variant="caption" fontWeight={600} sx={{ flexShrink: 0, pl: 1 }}>
              {entry.value}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};
